import React, { useEffect, useState } from "react";

import { TabMenu } from "primereact/tabmenu";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { ProgressSpinner } from "primereact/progressspinner";
import usePrimeLocale from "hooks/usePrimeLocale";
import { ObjectType } from "gql/generated/types";
import { StringKey } from "services/lang/keys";
import { MenuItem } from "primereact/menuitem";
import useProjectContext from "../hooks/useProjectContext";
import ProjectDropDown from "./ProjectDropDown";
import { useHasPermissions } from "../../../../hooks/useHasPermissions";
import ErrorPage from "../routes/ErrorPage";

const Layout = () => {
  usePrimeLocale();
  const path = useLocation().pathname;
  const { objectId, deviceId, maintenanceId, nodeId } = useParams();
  const [activeIndex, setActiveIndex] = useState(2);
  const {
    projectId,
    user,
    userSettings,
    project,
    projectLoading,
    userSettingsLoading,
    userSettingsError,
    projectError,
  } = useProjectContext();

  const isPermitted = useHasPermissions("USE_DATA_SERVICES");

  useEffect(() => {
    switch (path) {
      case `/mdm/${projectId}/objects`:
        return setActiveIndex(0);
      case `/mdm/${projectId}/nodes`:
        return setActiveIndex(1);
      case `/mdm/${projectId}/devices`:
        return setActiveIndex(2);
      case `/mdm/${projectId}/analytics`:
        return setActiveIndex(3);
      case `/mdm/${projectId}/jobs`:
        return setActiveIndex(4);
      case `/mdm/${projectId}/maintenance`:
        return setActiveIndex(5);
      case `/mdm/${projectId}/settings`:
        return setActiveIndex(6);
      case `/mdm/${projectId}/objects/${objectId}`:
        return setActiveIndex(7);
      case `/mdm/${projectId}/devices/${deviceId}`:
        return setActiveIndex(8);
      case `/mdm/${projectId}/maintenance/${maintenanceId}`:
        return setActiveIndex(9);
      case `/mdm/${projectId}/nodes/${nodeId}`:
        return setActiveIndex(10);
      default:
        return setActiveIndex(99);
    }
  }, [path, projectId, objectId, deviceId, maintenanceId, nodeId]);

  const objectNode = project?.mdmProjectById.configuration?.childrenConfiguration?.find(
    conf => conf?.childType === ObjectType.Child
  );

  const items: MenuItem[] = [
    {
      label: T("mdm.objects"),
      icon: "pi pi-list",
      url: `#/mdm/${projectId}/objects`,
    },
    {
      label: T((objectNode?.pluralName as StringKey) || "word.unknown-title", {
        defaultValue: objectNode?.pluralName,
      }),
      icon: "pi pi-list",
      url: `#/mdm/${projectId}/nodes`,
      visible: Boolean(objectNode?.visible),
    },
    {
      label: T("mdm.devices"),
      icon: "pi pi-list",
      url: `#/mdm/${projectId}/devices`,
    },
    {
      label: T("mdm.analytics"),
      icon: "pi pi-chart-line",
      url: `#/mdm/${projectId}/analytics`,
    },
    {
      label: T("mdm.jobs"),
      icon: "pi pi-briefcase",
      url: `#/mdm/${projectId}/jobs`,
    },
    {
      label: T("mdm.maintenance"),
      icon: "pi pi-building",
      url: `#/mdm/${projectId}/maintenance`,
    },
    {
      label: T("word.settings"),
      icon: "pi pi-cog",
      url: `#/mdm/${projectId}/settings`,
    },
    {
      label: T("word.object"),
      icon: "pi pi-book",
      url: `#/mdm/${projectId}/objects/${objectId}`,
      visible: !!objectId,
    },
    {
      label: T("word.device"),
      icon: "pi pi-book",
      url: `#/mdm/${projectId}/devices/${deviceId}`,
      visible: !!deviceId,
    },
    {
      label: T("mdm.maintenance"),
      icon: "pi pi-building",
      url: `#/mdm/${projectId}/maintenance/${maintenanceId}`,
      visible: !!maintenanceId,
    },
    {
      label: T((objectNode?.singularName as StringKey) || "word.unknown-title", {
        defaultValue: objectNode?.singularName,
      }),
      icon: "pi pi-building",
      url: `#/mdm/${projectId}/nodes/${nodeId}`,
      visible: !!nodeId,
    },
  ];

  if (!isPermitted) return <div>{T("message.missing-required-permissions")}</div>;

  return (
    <div className="bg-white p-0 sm:p-3 mdm-container">
      <div className="flex gap-1 flex-column-reverse md:flex-row">
        {projectId && (
          <TabMenu
            model={items}
            className="flex-1 justify-content-end"
            activeIndex={activeIndex}
            onTabChange={e => setActiveIndex(e.index)}
          />
        )}
        {user?.companyRef && <ProjectDropDown />}
      </div>
      {userSettings && project && <Outlet />}
      {(projectLoading || userSettingsLoading) && <ProgressSpinner className="w-full" />}
      {(userSettingsError || projectError) && (
        <ErrorPage msg={userSettingsError?.message ?? projectError?.message ?? ""} />
      )}
    </div>
  );
};

export default Layout;
